import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  FileText,
  Calendar,
  Search,
  Plus,
  ArrowRight,
  Clock,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  ChevronRight,
  FolderOpen,
  Folder,
  FolderPlus,
  Sparkles,
  Upload,
  BarChart3,
  ListChecks,
  Target,
  Loader,
  X,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { BentoGridItem } from '../components/BentoGrid';
import Particles from '../components/Particles';
import AddProjectModal, { ProjectFormData } from '../components/AddProjectModal';
import { ProjectApi, ProjectData } from '../services/projectApi';
import { AnalysisAPI } from '../services/api';
import { useToastNotifications } from '../context/ToastContext';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// =============================================================================
// PAGE SHELL — coherent with /profile, /settings, /knowledge-base, /tender-bank
// =============================================================================

const ProjectsContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) so the sticky toolbar inside the listing section can
     attach to the viewport top instead of being trapped here. */
  overflow: clip;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 580px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    align-items: stretch;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

type StatusTone = 'success' | 'warning' | 'primary';

const StatusPill = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-variant-numeric: tabular-nums;
  }

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    ${({ $tone }) =>
      $tone === 'success'
        ? css`
            background: ${theme.colors.success[500]};
            box-shadow: 0 0 0 4px ${theme.colors.success[500]}24;
          `
        : $tone === 'warning'
        ? css`
            background: ${theme.colors.warning[500]};
            box-shadow: 0 0 0 4px ${theme.colors.warning[500]}24;
          `
        : css`
            background: ${theme.colors.primary[500]};
            box-shadow: 0 0 0 4px ${theme.colors.primary[500]}24;
          `}
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

// =============================================================================
// STATS GRID
// =============================================================================

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }
`;

type StatTone = 'primary' | 'success' | 'warning' | 'accent';

const StatIconTile = styled.span<{ $tone: StatTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[600]};
          border-color: ${theme.colors.success[500]}40;
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[600]};
          border-color: ${theme.colors.warning[500]}40;
        `
      : $tone === 'accent'
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `
      : css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[500]};
          border-color: ${theme.colors.primary[100]};
        `}
`;

const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValueWrap = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing[1]};
`;

const StatValue = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 30px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.025em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;

const StatUnit = styled.span`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 18px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textMuted};
  letter-spacing: -0.015em;
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

// =============================================================================
// FIRST-RUN NAVY HERO (when no projects exist)
// =============================================================================

const FirstRunHero = styled.div`
  position: relative;
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.xl};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: rgba(255, 255, 255, 0.95);
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.primary[700]};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing[8]};
  text-align: left;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.045 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[8]} ${theme.spacing[5]};
  }
`;

const FirstRunEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.success[500]};
    box-shadow: 0 0 0 3px ${theme.colors.success[500]}33;
  }
`;

const FirstRunTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 32px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0 0 ${theme.spacing[2]};
  letter-spacing: -0.025em;
  line-height: 1.15;
  max-width: 640px;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 26px;
  }
`;

const FirstRunSub = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: ${theme.typography.fontSize.base};
  margin: 0 0 ${theme.spacing[6]};
  max-width: 580px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const FirstRunChipRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[6]};
  max-width: 820px;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FirstRunChip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: ${theme.borderRadius.lg};
  color: #fff;
  font-family: inherit;
  text-align: left;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  & .icon {
    width: 38px;
    height: 38px;
    border-radius: ${theme.borderRadius.md};
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  & .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  & .meta {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.65);
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  & .label {
    color: #fff;
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.sm};
    letter-spacing: -0.005em;
  }

  & .desc {
    margin-top: 2px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
  }
`;

const FirstRunFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
  padding-top: ${theme.spacing[5]};
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

const FirstRunCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: #fff;
  color: ${theme.colors.primary[700]};
  border: 1px solid #fff;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 0, 0.4);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 0, 0.5);
  }
`;

const FirstRunGhost = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

// =============================================================================
// LIBRARY SECTION
// =============================================================================

const LibrarySection = styled.section`
  margin-bottom: ${theme.spacing[8]};
`;

const SectionHead = styled.div`
  margin-bottom: ${theme.spacing[5]};
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 540px;
`;

// Sticky toolbar holding search + chips.
const StickyToolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 8;
  margin: 0 -${theme.spacing[4]} ${theme.spacing[5]};
  padding: ${theme.spacing[4]};
  background: rgba(248, 250, 251, 0.85);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid ${theme.colors.borderLight};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 240px;
  max-width: 480px;
`;

const SearchIconLeft = styled.span`
  position: absolute;
  left: ${theme.spacing[3]};
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[3]} ${theme.spacing[10]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  transition: all ${theme.transitions.base};

  &:hover:not(:focus) {
    border-color: ${theme.colors.borderHover};
    background: ${theme.colors.surfaceHover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.surface};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}1a, 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const FilterChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const FilterChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 7px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};

  ${({ $active }) =>
    $active
      ? css`
          background: ${theme.colors.primary[500]};
          color: #fff;
          border: 1px solid ${theme.colors.primary[600]};
          box-shadow: 0 4px 10px -4px rgba(0, 0, 41, 0.35);

          &:hover {
            background: ${theme.colors.primary[700]};
            transform: translateY(-1px);
          }
        `
      : css`
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};

          &:hover {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.borderHover};
            color: ${theme.colors.textPrimary};
          }
        `}
`;

const ChipCount = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  font-weight: ${theme.typography.fontWeight.bold};
  padding: 1px 6px;
  border-radius: ${theme.borderRadius.full};
  ${({ $active }) =>
    $active
      ? css`
          background: rgba(255, 255, 255, 0.22);
          color: #fff;
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textMuted};
        `}
`;

const SearchMatchInfo = styled.div`
  width: 100%;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-variant-numeric: tabular-nums;

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    font-size: 11px;
  }
`;

// =============================================================================
// PROJECT GRID + CARD
// =============================================================================

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: ${theme.spacing[4]};

  & > * {
    animation: ${fadeUp} 0.4s ease-out both;
  }

  /* Subtle stagger on the first ten cards. */
  & > *:nth-child(1) { animation-delay: 0ms; }
  & > *:nth-child(2) { animation-delay: 30ms; }
  & > *:nth-child(3) { animation-delay: 60ms; }
  & > *:nth-child(4) { animation-delay: 90ms; }
  & > *:nth-child(5) { animation-delay: 120ms; }
  & > *:nth-child(6) { animation-delay: 150ms; }
  & > *:nth-child(7) { animation-delay: 180ms; }
  & > *:nth-child(8) { animation-delay: 210ms; }
  & > *:nth-child(9) { animation-delay: 240ms; }
  & > *:nth-child(10) { animation-delay: 270ms; }

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  overflow: hidden;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${theme.colors.primary[500]} 0%,
      ${theme.colors.primary[700]} 100%
    );
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 12px 24px -12px rgba(0, 0, 41, 0.22);
  }

  &:hover::before {
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }

  &:hover .pc-arrow {
    transform: translateX(4px);
    color: ${theme.colors.primary[500]};
  }
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
`;

const FolderTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  border: 1px solid ${theme.colors.primary[100]};
  flex-shrink: 0;
`;

type ProjectStatusTone = 'completed' | 'in_progress' | 'pending' | 'error';

const StatusBadge = styled.div<{ $status: ProjectStatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.02em;
  border: 1px solid;
  flex-shrink: 0;
  text-transform: capitalize;

  ${({ $status }) => {
    switch ($status) {
      case 'completed':
        return css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
          border-color: ${theme.colors.success[500]}40;
        `;
      case 'in_progress':
        return css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
          border-color: ${theme.colors.warning[500]}40;
        `;
      case 'pending':
        return css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `;
      case 'error':
        return css`
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
          border-color: ${theme.colors.error[500]}40;
        `;
    }
  }}
`;

const ProjectTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.012em;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DateLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: 12px;
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;

  & .icon {
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
  }
`;

const ProjectDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(${theme.typography.fontSize.sm} * 1.625 * 3);
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

type PillTone = 'success' | 'primary' | 'warning' | 'muted';

const InlinePill = styled.span<{ $tone: PillTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  border: 1px solid;

  & .label {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 9px;
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: 0.18em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  & .icon {
    display: inline-flex;
    align-items: center;
    opacity: 0.7;
  }

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
          border-color: ${theme.colors.success[500]}40;
        `
      : $tone === 'primary'
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
          border-color: ${theme.colors.warning[500]}40;
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textMuted};
          border-color: ${theme.colors.borderLight};
        `}
`;

const PendingPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 4px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  background: ${theme.colors.backgroundTertiary};
  color: ${theme.colors.textMuted};
  border: 1px dashed ${theme.colors.borderLight};
  letter-spacing: 0.02em;
  font-style: italic;

  & .icon {
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${theme.spacing[3]};
  border-top: 1px dashed ${theme.colors.borderLight};
`;

const FooterMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const ViewDetails = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.primary[600]};
  letter-spacing: -0.005em;

  & .pc-arrow {
    transition: transform ${theme.transitions.base}, color ${theme.transitions.base};
  }
`;

// =============================================================================
// LOADING / ERROR / EMPTY (search)
// =============================================================================

const LoadingShell = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.borderLight};
  border-top-color: ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const ErrorBand = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]}33;
  border-left: 3px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.surface};
    color: ${theme.colors.error[600]};
    border: 1px solid ${theme.colors.error[500]}40;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.error[700]};
    margin: 0 0 ${theme.spacing[1]};
    letter-spacing: -0.01em;
  }

  & p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.error[700]};
    line-height: ${theme.typography.lineHeight.relaxed};
    margin: 0 0 ${theme.spacing[3]};
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.error[600]};
  color: #fff;
  border: 1px solid ${theme.colors.error[700]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.error[700]};
  }
`;

const SearchEmptyRow = styled.div`
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.surface};
  border: 1px dashed ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[3]};

  & .icon {
    width: 40px;
    height: 40px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    margin: 0;
    letter-spacing: -0.01em;
  }

  & p {
    margin: 0;
    max-width: 420px;
    line-height: ${theme.typography.lineHeight.relaxed};
  }

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    font-size: 11px;
  }
`;

const ClearFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[700]};
  }
`;

// =============================================================================
// TYPES (preserved verbatim)
// =============================================================================

interface EnhancedProjectData extends ProjectData {
  score: number;
  criteriaCount: number;
  responsesCount: number;
  analysisStatus: 'completed' | 'in_progress' | 'pending' | 'error';
}

const filters: Array<{
  key: 'all' | 'completed' | 'in_progress' | 'pending';
  label: string;
  icon: React.ReactNode;
}> = [
  { key: 'all', label: 'All', icon: <Folder size={12} /> },
  { key: 'completed', label: 'Completed', icon: <CheckCircle2 size={12} /> },
  { key: 'in_progress', label: 'In progress', icon: <Loader size={12} /> },
  { key: 'pending', label: 'Pending', icon: <Clock size={12} /> },
];

// =============================================================================
// COMPONENT
// =============================================================================

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToastNotifications();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [projects, setProjects] = useState<EnhancedProjectData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Function to fetch analysis data for a project and calculate metrics
  const fetchProjectAnalysisData = async (
    projectId: string
  ): Promise<{
    score: number;
    criteriaCount: number;
    responsesCount: number;
    analysisStatus: 'completed' | 'in_progress' | 'pending' | 'error';
  }> => {
    try {
      const response = await AnalysisAPI.listAnalysisSessions({
        project_id: projectId,
        page_size: 100,
      });

      if (response.success && response.data?.sessions?.length > 0) {
        // Get the most recent completed analysis
        const completedAnalyses = response.data.sessions.filter(
          (s: any) => s.status === 'completed'
        );
        const inProgressAnalyses = response.data.sessions.filter(
          (s: any) => s.status === 'running'
        );

        if (completedAnalyses.length > 0) {
          const latestAnalysis = completedAnalyses[0]; // Assuming sorted by date
          const results = latestAnalysis.results;

          // Calculate metrics from analysis results
          const criteriaCount = results?.evaluation_criteria?.length || 0;
          const responsesCount =
            results?.questions?.filter((q: any) => q.response && q.response.trim() !== '').length ||
            0;

          // Calculate score - prioritize refined/final score over initial score (same logic as ProjectDetailPage)
          const initialScore = results?.initial_review_result?.score || 0;
          let score = initialScore;

          // Try to extract final score from quality_check_result or final_report
          if (results?.quality_check_result?.score) {
            score = results.quality_check_result.score;
          } else if (results?.final_report) {
            // Try to parse final score from final_report string
            const scoreMatch = results.final_report.match(/overall score[:\s]*(\d+)/i);
            if (scoreMatch) {
              score = parseInt(scoreMatch[1]);
            }
          }

          return {
            score,
            criteriaCount,
            responsesCount,
            analysisStatus: 'completed',
          };
        } else if (inProgressAnalyses.length > 0) {
          return {
            score: 0,
            criteriaCount: 0,
            responsesCount: 0,
            analysisStatus: 'in_progress',
          };
        }
      }

      // No analysis data found
      return {
        score: 0,
        criteriaCount: 0,
        responsesCount: 0,
        analysisStatus: 'pending',
      };
    } catch (error) {
      console.error('Error fetching analysis data for project:', projectId, error);
      return {
        score: 0,
        criteriaCount: 0,
        responsesCount: 0,
        analysisStatus: 'error',
      };
    }
  };

  // Function to load projects and their analysis data
  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);

      // Fetch projects from API
      const projectsResponse = await ProjectApi.getProjects();

      if (projectsResponse.success && projectsResponse.data) {
        const rawProjects = projectsResponse.data.projects;

        // Enhance each project with analysis data
        const enhancedProjects = await Promise.all(
          rawProjects.map(async (project): Promise<EnhancedProjectData> => {
            const analysisData = await fetchProjectAnalysisData(project.id);

            return {
              ...project,
              ...analysisData,
            };
          })
        );

        setProjects(enhancedProjects);
      } else {
        setLoadingError(projectsResponse.error || 'Failed to load projects');
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setLoadingError('An unexpected error occurred while loading projects');
    } finally {
      setIsLoading(false);
    }
  };

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProjects = projects.filter(project => {
    const matchesSearch =
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === 'all' || project.analysisStatus === activeFilter;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={11} />;
      case 'in_progress':
        return <Loader size={11} />;
      case 'pending':
        return <Clock size={11} />;
      case 'error':
        return <AlertCircle size={11} />;
      default:
        return <FileText size={11} />;
    }
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleNewProject = () => {
    setIsAddModalOpen(true);
  };

  const handleCreateProject = async (projectData: ProjectFormData) => {
    setIsCreatingProject(true);

    try {
      // Call the real API to create the project
      const response = await ProjectApi.createProject({
        title: projectData.title,
        description: projectData.description,
        industry: projectData.industry,
        priority: projectData.priority,
        estimatedValue: projectData.estimatedValue,
        deadline: projectData.deadline,
        tags: [], // Default empty tags
      });

      if (response.success && response.data !== null) {
        // Create enhanced project with default analysis data
        const newProject: EnhancedProjectData = {
          ...response.data,
          score: 0,
          criteriaCount: 0,
          responsesCount: 0,
          analysisStatus: 'pending',
        };

        // Add to projects list
        setProjects(prev => [newProject, ...prev]);

        // Note: loadProjects is called to refresh data, but we avoid calling it immediately
        // after creation to prevent flickering. The new project will appear with default values.

        // Close modal
        setIsAddModalOpen(false);

        // Show success message
        console.log('Project created successfully:', newProject);
        showSuccess('Project Created', 'Your project has been created successfully!');

        // Note: User activity logging is handled by the backend API automatically
      } else {
        // Handle API error
        console.error('Failed to create project:', response.error);
        showError('Project Creation Failed', response.error || 'An unexpected error occurred');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      showError('Project Creation Failed', 'Failed to create project. Please try again.');
    } finally {
      setIsCreatingProject(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Score → tone mapping for the inline score pill
  const scoreToneFor = (s: number): PillTone => {
    if (s >= 80) return 'success';
    if (s >= 50) return 'primary';
    if (s >= 1) return 'warning';
    return 'muted';
  };

  // Aggregate counters per filter — drives the chip badges and stats grid.
  const counts = useMemo(() => {
    const total = projects.length;
    const completed = projects.filter(p => p.analysisStatus === 'completed').length;
    const inProgress = projects.filter(p => p.analysisStatus === 'in_progress').length;
    const pending = projects.filter(p => p.analysisStatus === 'pending').length;
    const error = projects.filter(p => p.analysisStatus === 'error').length;
    const completedScores = projects.filter(p => p.analysisStatus === 'completed').map(p => p.score);
    const avgScore =
      completedScores.length > 0
        ? Math.round(completedScores.reduce((sum, s) => sum + s, 0) / completedScores.length)
        : 0;
    return { total, completed, inProgress, pending, error, avgScore };
  }, [projects]);

  // Adaptive header status — three-way: success when there's analysis,
  // warning when everything is pending (nothing analysed yet), primary when empty.
  const headerStatus = useMemo<{ tone: StatusTone; label: React.ReactNode }>(() => {
    if (counts.total === 0) {
      return { tone: 'primary', label: <>No projects yet</> };
    }
    const hasAnalysis = counts.completed > 0 || counts.inProgress > 0;
    if (!hasAnalysis) {
      return {
        tone: 'warning',
        label: (
          <>
            <strong>{counts.total.toLocaleString()}</strong>{' '}
            {counts.total === 1 ? 'project' : 'projects'} · awaiting analysis
          </>
        ),
      };
    }
    return {
      tone: 'success',
      label: (
        <>
          <strong>{counts.total.toLocaleString()}</strong>{' '}
          {counts.total === 1 ? 'project' : 'projects'}
        </>
      ),
    };
  }, [counts]);

  const isFiltering = searchTerm.trim().length > 0 || activeFilter !== 'all';

  const handleClearFilters = () => {
    setSearchTerm('');
    setActiveFilter('all');
  };

  const filterCountFor = (key: 'all' | 'completed' | 'in_progress' | 'pending'): number => {
    if (key === 'all') return counts.total;
    if (key === 'completed') return counts.completed;
    if (key === 'in_progress') return counts.inProgress;
    return counts.pending;
  };

  return (
    <ProjectsContainer>
      <Particles
        particleCount={25}
        particleColor={theme.colors.primary[300]}
        speed={0.2}
        opacity={0.3}
      />
      <ContentWrapper>
        <Container>
          {/* ========================= PAGE HEADER ========================= */}
          <PageHeader>
            <HeaderLeft>
              <Crumb>Projects</Crumb>
              <Title>Projects</Title>
              <Subtitle>
                Track every tender analysis from intake to scored response. The AI works each
                project as a case file you can return to.
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <StatusPill $tone={headerStatus.tone}>
                <span className="pulse" />
                <span>{headerStatus.label}</span>
              </StatusPill>
              <ActionsRow>
                <PrimaryButton type="button" onClick={handleNewProject}>
                  <Plus size={16} />
                  New project
                </PrimaryButton>
              </ActionsRow>
            </HeaderRight>
          </PageHeader>

          {/* ========================= STATS GRID ========================= */}
          {!isLoading && !loadingError && counts.total > 0 && (
            <StatsGrid>
              <StatCard>
                <StatIconTile $tone="primary">
                  <Folder size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{counts.total.toLocaleString()}</StatValue>
                  </StatValueWrap>
                  <StatLabel>Total projects</StatLabel>
                </StatBody>
              </StatCard>
              <StatCard>
                <StatIconTile $tone="success">
                  <CheckCircle2 size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{counts.completed.toLocaleString()}</StatValue>
                  </StatValueWrap>
                  <StatLabel>Completed</StatLabel>
                </StatBody>
              </StatCard>
              <StatCard>
                <StatIconTile $tone="warning">
                  <Clock size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{counts.inProgress.toLocaleString()}</StatValue>
                  </StatValueWrap>
                  <StatLabel>In progress</StatLabel>
                </StatBody>
              </StatCard>
              <StatCard>
                <StatIconTile $tone="accent">
                  <BarChart3 size={16} />
                </StatIconTile>
                <StatBody>
                  <StatValueWrap>
                    <StatValue>{counts.avgScore}</StatValue>
                    <StatUnit>%</StatUnit>
                  </StatValueWrap>
                  <StatLabel>Average score</StatLabel>
                </StatBody>
              </StatCard>
            </StatsGrid>
          )}

          {/* ========================= MAIN CONTENT ========================= */}
          {isLoading ? (
            <LoadingShell>
              <LoadingSpinner />
              Loading your projects…
            </LoadingShell>
          ) : loadingError ? (
            <ErrorBand role="alert">
              <span className="icon">
                <AlertCircle size={16} />
              </span>
              <div className="body">
                <h4>We couldn't load your projects</h4>
                <p>{loadingError}</p>
                <RetryButton type="button" onClick={loadProjects}>
                  <Sparkles size={14} />
                  Try again
                </RetryButton>
              </div>
            </ErrorBand>
          ) : counts.total === 0 ? (
            /* First-run hero — the empty-projects moment. */
            <FirstRunHero>
              <FirstRunEyebrow>
                <span className="dot" />
                Getting started
              </FirstRunEyebrow>
              <FirstRunTitle>Start your first analysis project.</FirstRunTitle>
              <FirstRunSub>
                A project bundles a tender, the criteria the AI extracted, and your draft responses
                — all scored, refined, and exportable when you're ready to submit.
              </FirstRunSub>
              <FirstRunChipRow>
                <FirstRunChip>
                  <span className="icon">
                    <Upload size={18} />
                  </span>
                  <span className="body">
                    <span className="meta">Step 01</span>
                    <span className="label">Upload a tender</span>
                    <span className="desc">PDF or DOCX — the AI ingests it in seconds.</span>
                  </span>
                </FirstRunChip>
                <FirstRunChip>
                  <span className="icon">
                    <Sparkles size={18} />
                  </span>
                  <span className="body">
                    <span className="meta">Step 02</span>
                    <span className="label">AI extracts criteria</span>
                    <span className="desc">Questions, scoring weights, and evidence prompts.</span>
                  </span>
                </FirstRunChip>
                <FirstRunChip>
                  <span className="icon">
                    <CheckCircle2 size={18} />
                  </span>
                  <span className="body">
                    <span className="meta">Step 03</span>
                    <span className="label">Track your responses</span>
                    <span className="desc">Score, refine, and export ready-to-submit drafts.</span>
                  </span>
                </FirstRunChip>
              </FirstRunChipRow>
              <FirstRunFooter>
                <FirstRunCTA type="button" onClick={handleNewProject}>
                  <FolderPlus size={16} />
                  Create your first project
                </FirstRunCTA>
                <FirstRunGhost>Takes about a minute · cancel any time</FirstRunGhost>
              </FirstRunFooter>
            </FirstRunHero>
          ) : (
            <LibrarySection>
              <SectionHead>
                <Eyebrow>
                  <span className="num">01</span>
                  <span>·</span>
                  <span>Library</span>
                </Eyebrow>
                <SubHeading>Browse your projects</SubHeading>
                <SubHelper>
                  Search by name or description. Filter by status to focus on the ones that need
                  attention.
                </SubHelper>
              </SectionHead>

              <StickyToolbar>
                <SearchInputWrapper>
                  <SearchIconLeft>
                    <Search size={16} />
                  </SearchIconLeft>
                  <SearchInput
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search projects by name or description…"
                    aria-label="Search projects"
                  />
                </SearchInputWrapper>
                <FilterChipsRow>
                  {filters.map(filter => {
                    const active = activeFilter === filter.key;
                    return (
                      <FilterChip
                        key={filter.key}
                        type="button"
                        $active={active}
                        onClick={() => setActiveFilter(filter.key)}
                      >
                        {filter.icon}
                        {filter.label}
                        <ChipCount $active={active}>{filterCountFor(filter.key)}</ChipCount>
                      </FilterChip>
                    );
                  })}
                </FilterChipsRow>
                {searchTerm.trim().length > 0 && (
                  <SearchMatchInfo>
                    {filteredProjects.length}{' '}
                    {filteredProjects.length === 1 ? 'match' : 'matches'} for{' '}
                    <code>{searchTerm.trim()}</code>
                  </SearchMatchInfo>
                )}
              </StickyToolbar>

              {filteredProjects.length === 0 ? (
                <SearchEmptyRow>
                  <span className="icon">
                    <Search size={20} />
                  </span>
                  <h4>No projects match your filters</h4>
                  <p>
                    {searchTerm.trim() ? (
                      <>
                        Nothing found for <code>{searchTerm.trim()}</code>. Try a broader query or
                        reset your filters.
                      </>
                    ) : (
                      <>
                        Nothing matches the current filter. Try resetting to{' '}
                        <code>All</code>.
                      </>
                    )}
                  </p>
                  {isFiltering && (
                    <ClearFiltersButton type="button" onClick={handleClearFilters}>
                      <X size={14} />
                      Clear filters
                    </ClearFiltersButton>
                  )}
                </SearchEmptyRow>
              ) : (
                <ProjectsGrid>
                  {filteredProjects.map(project => {
                    const isPending = project.analysisStatus === 'pending';
                    const statusLabel = project.analysisStatus
                      .replace('_', ' ');
                    return (
                      <ProjectCard
                        key={project.id}
                        type="button"
                        onClick={() => handleProjectClick(project.id)}
                        aria-label={`Open ${project.name}`}
                      >
                        <CardTopRow>
                          <FolderTile>
                            <FolderOpen size={18} />
                          </FolderTile>
                          <StatusBadge $status={project.analysisStatus}>
                            {getStatusIcon(project.analysisStatus)}
                            {statusLabel}
                          </StatusBadge>
                        </CardTopRow>

                        <ProjectTitle>{project.name}</ProjectTitle>

                        <DateLine>
                          <span className="icon">
                            <Calendar size={11} />
                          </span>
                          Created{' '}
                          {formatDate(
                            typeof project.created_at === 'string'
                              ? project.created_at.split('T')[0]
                              : new Date(project.created_at).toISOString().split('T')[0]
                          )}
                        </DateLine>

                        <ProjectDescription>
                          {project.description || 'No description available'}
                        </ProjectDescription>

                        <StatsRow>
                          {isPending ? (
                            <PendingPill>
                              <span className="icon">
                                <Clock size={11} />
                              </span>
                              No analysis yet
                            </PendingPill>
                          ) : (
                            <>
                              <InlinePill $tone={scoreToneFor(project.score)}>
                                <span className="icon">
                                  <Target size={11} />
                                </span>
                                {project.score}
                                <span className="label">Score</span>
                              </InlinePill>
                              <InlinePill $tone="muted">
                                <span className="icon">
                                  <ListChecks size={11} />
                                </span>
                                {project.criteriaCount}
                                <span className="label">Criteria</span>
                              </InlinePill>
                              <InlinePill $tone="muted">
                                <span className="icon">
                                  <FileText size={11} />
                                </span>
                                {project.responsesCount}
                                <span className="label">Responses</span>
                              </InlinePill>
                            </>
                          )}
                        </StatsRow>

                        <CardFooter>
                          <FooterMeta>
                            Updated{' '}
                            {formatDate(
                              typeof project.updated_at === 'string'
                                ? project.updated_at.split('T')[0]
                                : new Date(project.updated_at).toISOString().split('T')[0]
                            )}
                          </FooterMeta>
                          <ViewDetails>
                            View details
                            <ArrowRight size={12} className="pc-arrow" />
                          </ViewDetails>
                        </CardFooter>
                      </ProjectCard>
                    );
                  })}
                </ProjectsGrid>
              )}
            </LibrarySection>
          )}
        </Container>
      </ContentWrapper>

      {/* Add Project Modal */}
      <AddProjectModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreateProject}
        isLoading={isCreatingProject}
      />
    </ProjectsContainer>
  );
};

export default ProjectsPage;
